<template>
  <div class="pb-8">
    <v-toolbar color="grey-darken-4" flat theme="dark" density="compact">
      <v-toolbar-title class="admin-summary-toolbar-title">
        <span>{{ $t("home.admin_summary.title") }}</span>
       
      </v-toolbar-title>
    </v-toolbar>

    <v-card flat class="ma-2">
      <v-card-text>
        <v-row :class="$vuetify.display.xs ? '' : 'pl-2'">
          <!-- Mobile: compact controls -->
          <v-col v-if="$vuetify.display.xs" cols="12" class="pa-2 pb-0">
            <v-row align="center" class="px-1" no-gutters>
              <v-col class="pr-1">
                <v-select
                  v-model="summary.timePeriod"
                  :items="timePeriodItems"
                  :label="$t('home.charts.time_period')"
                  variant="outlined"
                  density="compact"
                  hide-details
                  item-title="title"
                  item-value="value"
                  color="red-darken-2"
                />
              </v-col>
              <v-col class="px-1">
                <v-menu v-model="mobileDateMenu" :close-on-content-click="false">
                  <template #activator="{ props }">
                    <v-text-field
                      v-bind="props"
                      :model-value="formattedDate"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      hide-details
                      variant="outlined"
                      density="compact"
                    />
                  </template>
                  <v-date-picker
                    v-model="pickerDate"
                    :type="summary.timePeriod === 'Month' ? 'month' : 'date'"
                    color="red-darken-2"
                    reactive
                    @update:model-value="mobileDateMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col cols="auto">
                <v-btn icon variant="text" @click="fetchSummary">
                  <v-icon color="grey-darken-2">mdi-orbit-variant</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-col>

          <!-- Desktop: date picker column -->
          <v-col v-if="!$vuetify.display.xs" cols="12" md="4" class="pa-4 pl-8">
            <v-row>
              <v-col md="8" cols="auto" class="flex-grow-1">
                <v-select
                  v-model="summary.timePeriod"
                  :items="timePeriodItems"
                  :label="$t('home.charts.time_period')"
                  :hint="$t('home.charts.select_time_period')"
                  variant="solo"
                  flat
                  item-title="title"
                  item-value="value"
                  persistent-hint
                  color="red-darken-2"
                  background-color="grey-lighten-4"
                />
              </v-col>
              <v-col cols="auto">
                <v-btn variant="text" icon @click="fetchSummary">
                  <v-icon color="grey-darken-2">mdi-orbit-variant</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row justify="center">
              <v-date-picker
                v-model="pickerDate"
                :type="summary.timePeriod === 'Month' ? 'month' : 'date'"
                color="red-darken-2"
                reactive
              />
            </v-row>
          </v-col>

          <v-col cols="12" :md="$vuetify.display.xs ? 12 : 8">
            <v-alert
              v-if="error"
              type="error"
              variant="tonal"
              border="bottom"
              class="mb-4"
            >
              {{ error }}
            </v-alert>

            <v-row v-if="summary.loading" justify="center" class="py-8">
              <v-progress-circular size="70" color="red" indeterminate />
            </v-row>

            <template v-else-if="hasData">
              <!-- Summary cards -->
              <v-row class="mb-4">
                <v-col
                  v-for="card in summaryCards"
                  :key="card.key"
                  cols="6"
                  sm="6"
                  md="3"
                  class="pa-1"
                >
                  <v-card :color="card.color" flat>
                    <v-card-text class="pa-3">
                      <div
                        :class="
                          $vuetify.display.xs
                            ? 'text-h6 font-weight-bold text-white'
                            : 'text-h5 font-weight-bold text-white'
                        "
                      >
                        {{ card.value }}
                      </div>
                      <div
                        :class="
                          $vuetify.display.xs
                            ? 'text-caption text-white'
                            : 'text-body-2 text-white'
                        "
                      >
                        {{ card.label }}
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Stacked bar chart -->
              <v-card flat color="grey-lighten-4" class="mb-4 pa-2">
                <v-card-title class="text-subtitle-1">
                  {{ $t("home.admin_summary.by_institution") }}
                </v-card-title>
                <div class="admin-chart-scroll">
                  <div class="admin-chart-inner" :style="chartContainerStyle">
                    <chart
                      :key="`bar-${chartKey}`"
                      type="bar"
                      stacked
                      :data="stackedBarData"
                      :height="barChartHeight"
                      :extra-options="barChartExtraOptions"
                    />
                  </div>
                </div>
              </v-card>
            </template>
          </v-col>

          <!-- Full-width institution breakdown below calendar + chart -->
          <v-col v-if="!summary.loading && hasData" cols="12">
            <v-card flat color="grey-lighten-4" class="pa-2">
              <v-card-title class="text-subtitle-1">
                {{ $t("home.admin_summary.institution_table") }}
                <br />
                <span v-if="selectedPeriodLabel" class="admin-summary-period">
                  {{ selectedPeriodLabel }}
                </span>
              </v-card-title>

              <!-- Mobile: institution cards -->
              <div v-if="$vuetify.display.xs" class="px-2 pb-2">
                <v-card
                  v-for="row in tableRows"
                  :key="row.institutionId"
                  variant="outlined"
                  class="mb-2 institution-stat-card"
                >
                  <v-card-text class="pa-3">
                    <div class="text-subtitle-2 font-weight-bold mb-2">
                      {{ row.institutionTitle }}
                    </div>
                    <div
                      v-for="field in institutionStatFields"
                      :key="field.key"
                      class="d-flex justify-space-between align-center py-1 institution-stat-row"
                      :class="{ 'font-weight-medium': field.key === 'total' }"
                    >
                      <span class="text-body-2 text-medium-emphasis pr-2">
                        {{ field.label }}
                      </span>
                      <span class="text-body-2">{{ row[field.key] }}</span>
                    </div>
                  </v-card-text>
                </v-card>
              </div>

              <!-- Desktop: table -->
              <div v-else class="admin-table-scroll">
                <v-table density="compact" class="admin-summary-table">
                  <thead>
                    <tr>
                      <th class="institution-title-cell">
                        {{ $t("home.institution_title") }}
                      </th>
                      <th
                        v-for="field in institutionStatFields"
                        :key="field.key"
                        class="text-end count-cell"
                      >
                        {{ field.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in tableRows"
                      :key="row.institutionId"
                    >
                      <td class="institution-title-cell">
                        {{ row.institutionTitle }}
                      </td>
                      <td
                        v-for="field in institutionStatFields"
                        :key="field.key"
                        class="text-end count-cell"
                        :class="{ 'font-weight-medium': field.key === 'total' }"
                      >
                        {{ row[field.key] }}
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import moment from "moment";
import getRequest from "../../api/getRequest";
import Chart from "../chart.vue";

const APPROVAL_STATUSES = "1.1,1.2,1.3,1.4";

function formatLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatLocalMonthString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function parseLocalDateString(str, timePeriod) {
  if (timePeriod === "Month") {
    const [y, m] = str.split("-").map(Number);
    return new Date(y, m - 1, 1);
  }
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function todayLocalDateString() {
  return formatLocalDateString(new Date());
}

const STATUS_SERIES = [
  {
    key: "ApprovedParts",
    label: "ApprovedParts",
    color: "rgba(76, 175, 80, 0.85)",
    borderColor: "rgba(76, 175, 80, 1)",
  },
  {
    key: "RefusedParts",
    label: "RefusedParts",
    color: "rgba(244, 67, 54, 0.85)",
    borderColor: "rgba(244, 67, 54, 1)",
  },
  {
    key: "PartsSentForReview",
    label: "PartsSentForReview",
    color: "rgba(255, 152, 0, 0.85)",
    borderColor: "rgba(255, 152, 0, 1)",
  },
  {
    key: "PartsSentForApproval",
    label: "PartsSentForApproval",
    color: "rgba(33, 150, 243, 0.85)",
    borderColor: "rgba(33, 150, 243, 1)",
  },
];

export default {
  components: { chart: Chart },
  data: () => ({
    summary: {
      loading: false,
      timePeriod: "Week",
      time: todayLocalDateString(),
      totalsByStatus: null,
      byInstitution: [],
    },
    timePeriodItems: [
      { title: "Month", value: "Month" },
      { title: "Week", value: "Week" },
      { title: "Day", value: "Day" },
    ],
    mobileDateMenu: false,
    error: null,
    chartKey: 0,
  }),
  computed: {
    formattedDate() {
      if (!this.summary.time) return "";
      let date;
      if (this.summary.time instanceof Date) {
        date = this.summary.time;
      } else {
        const str = this.summary.time;
        date = parseLocalDateString(str, this.summary.timePeriod);
      }
      if (isNaN(date.getTime())) return "";
      return this.summary.timePeriod === "Month"
        ? date.toLocaleDateString(undefined, {
            month: "short",
            year: "numeric",
          })
        : date.toLocaleDateString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
    },
    selectedPeriodLabel() {
      const periodItem = this.timePeriodItems.find(
        (item) => item.value === this.summary.timePeriod,
      );
      const periodTitle = periodItem?.title || this.summary.timePeriod;
      if (!this.formattedDate) return periodTitle;
      return `${periodTitle} · ${this.formattedDate}`;
    },
    pickerDate: {
      get() {
        if (!this.summary.time) return null;
        if (this.summary.time instanceof Date) return this.summary.time;
        return parseLocalDateString(
          this.summary.time,
          this.summary.timePeriod,
        );
      },
      set(value) {
        if (!value || !(value instanceof Date)) return;
        if (this.summary.timePeriod === "Month") {
          this.summary.time = formatLocalMonthString(value);
        } else {
          this.summary.time = formatLocalDateString(value);
        }
      },
    },
    hasData() {
      return (
        this.summary.totalsByStatus !== null &&
        Array.isArray(this.summary.byInstitution)
      );
    },
    summaryCards() {
      const totals = this.summary.totalsByStatus || {};
      return [
        {
          key: "approved",
          label: this.$t("part_status.approved_parts"),
          value: totals.ApprovedParts ?? 0,
          color: "green-darken-1",
        },
        {
          key: "refused",
          label: this.$t("part_status.refused_parts"),
          value: totals.RefusedParts ?? 0,
          color: "red-darken-1",
        },
        {
          key: "review",
          label: this.$t("part_status.parts_sent_for_review"),
          value: totals.PartsSentForReview ?? 0,
          color: "orange-darken-2",
        },
        {
          key: "pending",
          label: this.$t("part_status.parts_sent_for_approval"),
          value: totals.PartsSentForApproval ?? 0,
          color: "blue-darken-1",
        },
      ];
    },
    stackedBarData() {
      const institutions = this.summary.byInstitution || [];
      return {
        labels: institutions.map((i) => i.institutionTitle),
        datasets: STATUS_SERIES.map((series) => ({
          label: series.label,
          backgroundColor: series.color,
          borderColor: series.borderColor,
          borderWidth: 1,
          data: institutions.map((i) => i.byStatus?.[series.key] ?? 0),
        })),
      };
    },
    donutData() {
      const totals = this.summary.totalsByStatus || {};
      return {
        labels: STATUS_SERIES.map((s) => s.label),
        datasets: [
          {
            data: STATUS_SERIES.map((s) => totals[s.key] ?? 0),
            backgroundColor: STATUS_SERIES.map((s) => s.color),
            borderColor: STATUS_SERIES.map((s) => s.borderColor),
            borderWidth: 1,
          },
        ],
      };
    },
    tableRows() {
      return (this.summary.byInstitution || []).map((inst) => ({
        institutionId: inst.institutionId,
        institutionTitle: inst.institutionTitle,
        approved: inst.byStatus?.ApprovedParts ?? 0,
        refused: inst.byStatus?.RefusedParts ?? 0,
        review: inst.byStatus?.PartsSentForReview ?? 0,
        pending: inst.byStatus?.PartsSentForApproval ?? 0,
        total: inst.total ?? 0,
      }));
    },
    institutionStatFields() {
      return [
        {
          key: "approved",
          label: this.$t("part_status.approved_parts"),
        },
        {
          key: "refused",
          label: this.$t("part_status.refused_parts"),
        },
        {
          key: "review",
          label: this.$t("part_status.parts_sent_for_review"),
        },
        {
          key: "pending",
          label: this.$t("part_status.parts_sent_for_approval"),
        },
        {
          key: "total",
          label: this.$t("home.admin_summary.total"),
        },
      ];
    },
    barChartHeight() {
      return this.$vuetify.display.xs ? 380 : 320;
    },
    chartMinWidth() {
      const count = (this.summary.byInstitution || []).length;
      const perBar = this.$vuetify.display.xs ? 72 : 56;
      return Math.max(count * perBar, 280);
    },
    chartContainerStyle() {
      const width = this.chartMinWidth;
      const useScroll = width > 320 || this.$vuetify.display.xs;
      return {
        minWidth: `${width}px`,
        width: useScroll ? `${width}px` : "100%",
        height: `${this.barChartHeight}px`,
      };
    },
    barChartExtraOptions() {
      if (!this.$vuetify.display.xs) return {};
      return {
        legend: {
          position: "bottom",
          labels: { boxWidth: 10, fontSize: 10, padding: 10 },
        },
        scales: {
          xAxes: [
            {
              stacked: true,
              barPercentage: 0.85,
              categoryPercentage: 0.9,
              maxBarThickness: 36,
              ticks: {
                maxRotation: 90,
                minRotation: 45,
                autoSkip: false,
                fontSize: 9,
              },
            },
          ],
          yAxes: [
            {
              stacked: true,
              ticks: { beginAtZero: true, maxTicksLimit: 6, fontSize: 10 },
            },
          ],
        },
      };
    },
  },
  watch: {
    "summary.time"(value) {
      if (value instanceof Date) return;
      if (value && !this.summary.loading) {
        this.fetchSummary();
      }
    },
    "summary.timePeriod"() {
      if (this.summary.time && !this.summary.loading) {
        this.fetchSummary();
      }
    },
  },
  mounted() {
    this.getLocale();
    this.fetchSummary();
  },
  methods: {
    getLocale() {
      this.timePeriodItems[0].title = this.$t(
        "home.charts.time_period_items.month",
      );
      this.timePeriodItems[1].title = this.$t(
        "home.charts.time_period_items.week",
      );
      this.timePeriodItems[2].title = this.$t(
        "home.charts.time_period_items.day",
      );
    },
    getApiTime() {
      const { timePeriod, time } = this.summary;
      let m;
      if (timePeriod === "Month") {
        const monthValue =
          typeof time === "string" && time.length === 7 ? `${time}-01` : time;
        m = moment(monthValue, "YYYY-MM-DD").startOf("month");
      } else {
        m = moment(time, "YYYY-MM-DD").startOf(
          timePeriod === "Week" ? "week" : "day",
        );
      }
      return m.toISOString();
    },
    async fetchSummary() {
      const role = this.$store.getters.role;
      if (role !== "LekoAdmin" && role !== "SuperAdmin") return;

      this.summary.loading = true;
      this.error = null;
      try {
        const response = await getRequest(
          "/history/admin/summary",
          `status=${APPROVAL_STATUSES}&period=${this.summary.timePeriod}&time=${this.getApiTime()}`,
        );
        this.summary.totalsByStatus = response.totalsByStatus || {};
        this.summary.byInstitution = response.byInstitution || [];
        this.chartKey += 1;
      } catch (err) {
        this.summary.totalsByStatus = null;
        this.summary.byInstitution = [];
        if (err?.response?.status === 403) {
          this.error = this.$t("home.admin_summary.not_authorized");
        } else {
          this.error = this.$t("home.admin_summary.error");
        }
      } finally {
        this.summary.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-summary-toolbar-title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  line-height: 1.3;
}

.admin-summary-period {
  font-size: 0.8rem;
  font-weight: 400;
  opacity: 0.75;
}

.admin-chart-scroll,
.admin-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
}

.admin-chart-inner {
  position: relative;
}

.admin-summary-table {
  table-layout: fixed;
  width: 100%;
  min-width: 330px;
}

.admin-summary-table th,
.admin-summary-table td {
  vertical-align: top;
}

.admin-summary-table td.count-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.admin-summary-table .institution-title-cell,
.admin-summary-table th.count-cell {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: break-word;
  line-height: 1.2;
}

.admin-summary-table .institution-title-cell {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
}

.admin-summary-table .count-cell {
  width: 50px;
  min-width: 50px;
  max-width: 50px;
}

.institution-stat-card {
  background: white;
}

.institution-stat-row + .institution-stat-row {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
